import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { AlertTriangle, FileDown, History, Printer } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, domaineLabel } from "@/lib/api";
import { Button } from "@/components/ui/button";

const qrUrl = (m) =>
  `${window.location.origin}/declarer/${m.domaine}?site=${encodeURIComponent(m.site_id || "s1")}&materiel=${encodeURIComponent(m.designation)}&qr=1`;

const CarteQr = ({ m, contact, aRisque, onHistorique }) => (
  <div data-testid={`qr-card-${m.designation}`} className="rounded-xl border border-slate-300 p-4 text-center bg-white">
    <QRCodeSVG value={qrUrl(m)} size={128} level="M" includeMargin className="mx-auto" />
    <div className="text-sm font-bold text-slate-900 mt-3">{m.designation}</div>
    {aRisque && (
      <div
        data-testid={`equip-risque-${m.designation}`}
        className="inline-flex items-center gap-1 mt-1.5 rounded-full bg-red-50 border border-red-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-red-700"
      >
        <AlertTriangle size={10} /> À risque
      </div>
    )}
    <div className="text-xs text-slate-500">
      {domaineLabel(m.domaine)} · {contact.sites?.find((s) => s.id === m.site_id)?.nom || "Site principal"}
    </div>
    {(m.numero_serie || m.date_pose) && (
      <div className="text-[11px] text-slate-400 mt-1">
        {m.numero_serie ? `SN ${m.numero_serie}` : ""}
        {m.numero_serie && m.date_pose ? " · " : ""}
        {m.date_pose ? `posé le ${m.date_pose}` : ""}
      </div>
    )}
    <button
      data-testid={`equip-historique-${m.id || m.designation}`}
      onClick={() => onHistorique(m)}
      className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#005CFF] hover:underline"
    >
      <History size={13} /> Historique des pannes
    </button>
    <div className="text-[10px] text-slate-400 mt-2">Scannez pour déclarer une panne — BKPROD</div>
  </div>
);

export const ClientEtiquettes = ({ contact, axonautId, estARisque, onHistorique }) => {
  const [planche, setPlanche] = useState({ site_id: "", gabarit: "avery21", type_planche: "materiel" });

  const telechargerPlanche = async () => {
    try {
      const { data } = await api.get(`/admin/clients/${axonautId}/etiquettes`, {
        params: { gabarit: planche.gabarit, type_planche: planche.type_planche, ...(planche.site_id ? { site_id: planche.site_id } : {}) },
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `etiquettes-qr-${axonautId}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 20000);
      toast.success("Planche PDF générée");
    } catch (e) {
      const detail = e.response?.data instanceof Blob ? await e.response.data.text() : null;
      toast.error(
        detail?.includes("Aucun matériel") || detail?.includes("Aucun élément")
          ? "Aucun élément à étiqueter pour cette sélection"
          : errMsg(e, "Génération impossible")
      );
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="bk-label">Étiquettes QR à coller sur les équipements</div>
          <p className="text-xs text-slate-500 mt-1">
            Générez une planche PDF prête à imprimer sur planches autocollantes, pour tout le chantier ou un site précis.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            data-testid="planche-site-select"
            value={planche.site_id}
            onChange={(e) => setPlanche({ ...planche, site_id: e.target.value })}
            className="h-9 border border-slate-200 rounded-lg px-2 text-sm bg-white"
          >
            <option value="">Tous les sites</option>
            {contact.sites?.map((s) => (
              <option key={s.id} value={s.id}>{s.nom}</option>
            ))}
          </select>
          <select
            data-testid="planche-gabarit-select"
            value={planche.gabarit}
            onChange={(e) => setPlanche({ ...planche, gabarit: e.target.value })}
            className="h-9 border border-slate-200 rounded-lg px-2 text-sm bg-white"
          >
            <option value="avery21">Avery L7160 (21 par page)</option>
            <option value="grand">Grand format (8 par page)</option>
          </select>
          <select
            data-testid="planche-type-select"
            value={planche.type_planche}
            onChange={(e) => setPlanche({ ...planche, type_planche: e.target.value })}
            className="h-9 border border-slate-200 rounded-lg px-2 text-sm bg-white"
          >
            <option value="materiel">Une étiquette par matériel</option>
            <option value="site">Une étiquette par chantier</option>
            <option value="les_deux">Matériel + chantier</option>
          </select>
          <Button data-testid="planche-pdf-btn" size="sm" className="bg-[#005CFF] hover:bg-[#0047CC]" onClick={telechargerPlanche}>
            <FileDown size={14} className="mr-1" /> Planche PDF
          </Button>
          <Button data-testid="qr-print-btn" variant="outline" size="sm" onClick={() => window.print()}>
            <Printer size={14} className="mr-1" /> Imprimer l'écran
          </Button>
        </div>
      </div>
      {!contact.materiel?.length && <p className="text-sm text-slate-500">Ajoutez du matériel pour générer les QR codes.</p>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {contact.materiel?.map((m) => (
          <CarteQr
            key={m.id || `${m.site_id}-${m.designation}`}
            m={m}
            contact={contact}
            aRisque={estARisque(m)}
            onHistorique={onHistorique}
          />
        ))}
      </div>
    </div>
  );
};

export default ClientEtiquettes;
